import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, Clock, XCircle, Play } from 'lucide-react';
import { format } from 'date-fns';

const StatusBadge = ({ status }) => {
    const styles = {
        APPROVED: "bg-green-100 text-green-700 border-green-200",
        PENDING: "bg-yellow-100 text-yellow-700 border-yellow-200",
        REJECTED: "bg-red-100 text-red-700 border-red-200",
    };

    const icons = {
        APPROVED: <CheckCircle2 size={14} className="mr-1" />,
        PENDING: <Clock size={14} className="mr-1" />,
        REJECTED: <XCircle size={14} className="mr-1" />,
    };

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}>
            {icons[status]}
            {status}
        </span>
    );
};

const ApprovalRow = ({ item, onExecute, isExecuting }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const approvalId = item?.approval_id ?? item?.id;
    const approvedOn = item?.approved_on ?? item?.date;

    return (
        <>
            <tr className="hover:bg-gray-50 border-b border-gray-100 transition-colors">
                <td className="py-4 pr-2 pl-4 text-sm font-medium text-gray-900">{approvalId ?? '-'}</td>
                <td className="py-4 px-2 text-sm text-gray-600">{item?.site ?? '-'}</td>
                <td className="py-4 px-2 text-sm font-semibold text-gray-900">
                    ₹{item.amount?.toLocaleString()}
                </td>
                <td className="py-4 px-2 text-sm text-gray-600">{item.payment_type}</td>
                <td className="py-4 px-2 text-sm text-gray-600">{item.category}</td>
                <td className="py-4 px-2">
                    <StatusBadge status={item.status} />
                </td>
                <td className="py-4 px-2 text-sm text-gray-600">{item.approved_by || '-'}</td>
                <td className="py-4 px-2 text-sm text-gray-500 whitespace-nowrap">
                    {approvedOn ? format(new Date(approvedOn), 'MMM d, yyyy • hh:mm a') : '-'}
                </td>
                <td className="py-4 px-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                        {item.status === 'APPROVED' && (
                            item.is_executed ? (
                                <span className="flex items-center text-green-600 text-xs font-medium">
                                    <CheckCircle2 size={16} className="mr-1" /> Completed
                                </span>
                            ) : (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onExecute(approvalId, item.category);
                                    }}
                                    disabled={Boolean(isExecuting) || approvalId == null}
                                    className="inline-flex items-center px-3 py-1.5 bg-sm-primary text-white text-xs font-semibold rounded-lg hover:bg-sm-hover transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <Play size={12} className="mr-1 fill-current" /> {isExecuting ? "Executing..." : "Execute"}
                                </button>
                            )
                        )}
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-1 hover:bg-gray-200 rounded-lg text-gray-400 transition-colors"
                        >
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                    </div>
                </td>
            </tr>
            {isExpanded && (
                <tr className="bg-gray-50">
                    <td colSpan="9" className="py-4 px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Remarks</h4>
                                <p className="text-sm text-gray-700">{item.remarks || "No remarks provided."}</p>
                            </div>
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Transaction Payload</h4>
                                <div className="bg-white p-3 rounded-lg border border-gray-200 text-xs font-mono text-gray-600 max-h-32 overflow-y-auto">
                                    <pre>{JSON.stringify(item.transaction_payload, null, 2)}</pre>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const PettyCashApprovals = ({ approvals, onExecute, executeLoadingById }) => {
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-lg font-semibold text-gray-900">My Requested Approvals</h2>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                            <th className="py-3 pr-2 pl-4">Approval ID</th>
                            <th className="py-3 px-2">Site</th>
                            <th className="py-3 px-2">Amount</th>
                            <th className="py-3 px-2">Payment Type</th>
                            <th className="py-3 px-2">Category</th>
                            <th className="py-3 px-2">Status</th>
                            <th className="py-3 px-2">Approved By</th>
                            <th className="py-3 px-2">Date</th>
                            <th className="py-3 px-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {approvals?.length > 0 ? (
                            approvals.map((item) => (
                                <ApprovalRow
                                    key={item?.approval_id ?? item?.id}
                                    item={item}
                                    onExecute={onExecute}
                                    isExecuting={Boolean(executeLoadingById?.[item?.approval_id ?? item?.id])}
                                />
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="py-12 text-center text-gray-500 italic">
                                    No approvals found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PettyCashApprovals;
